import { useEffect, useMemo, useState } from 'react';
import { type PublicMood, usePublicMoodRecommendations, usePublicMoods } from './discoveryApi';
import { MoodCard } from './MoodCard';
import { MoodSuggestion } from './MoodSuggestion';
import { useSetCartMood } from '@/features/cart/cartApi';
import { useCartWorkflowStore } from '@/state/cartWorkflowStore';
import {
  resolveDefaultStorefrontMood,
  toStorefrontMood,
  toStorefrontMoodRecommendation,
  type StorefrontMood,
} from './moods';

export function MoodSelector({
  moods,
  loading,
  error,
  onRetry,
  selectedCode: controlledSelectedCode,
  onSelectionChange,
  showSuggestions = true,
}: {
  moods?: PublicMood[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  selectedCode?: string | null;
  onSelectionChange?: (mood: StorefrontMood) => void;
  /** Selection-only contexts persist the mood without loading or rendering recommended products. */
  showSuggestions?: boolean;
}) {
  const shouldLoadMoods = moods === undefined;
  const internalMoods = usePublicMoods({ enabled: shouldLoadMoods });
  const sourceMoods = useMemo(() => moods ?? internalMoods.data ?? [], [internalMoods.data, moods]);
  const isLoading = loading ?? (shouldLoadMoods && internalMoods.isPending);
  const isError = error ?? (shouldLoadMoods && internalMoods.isError);
  const retry = onRetry ?? (() => void internalMoods.refetch());
  const setSelectedMood = useCartWorkflowStore((state) => state.setSelectedMood);
  const setCartMood = useSetCartMood();

  const storefrontMoods = useMemo(
    () => sourceMoods.map(toStorefrontMood).filter((mood): mood is StorefrontMood => Boolean(mood)),
    [sourceMoods]
  );
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const defaultMood = resolveDefaultStorefrontMood(storefrontMoods);
  const effectiveSelectedCode = controlledSelectedCode ?? selectedCode;
  const selected =
    storefrontMoods.find((mood) => mood.code === effectiveSelectedCode) ?? defaultMood;
  const recommendationsQuery = usePublicMoodRecommendations(selected?.code, {
    limit: 6,
    enabled: showSuggestions && Boolean(selected),
  });
  const recommendations = useMemo(
    () =>
      (recommendationsQuery.data ?? [])
        .map(toStorefrontMoodRecommendation)
        .filter((item): item is NonNullable<typeof item> => Boolean(item)),
    [recommendationsQuery.data]
  );

  useEffect(() => {
    if (controlledSelectedCode !== undefined) return;
    if (selectedCode && storefrontMoods.some((mood) => mood.code === selectedCode)) return;
    setSelectedCode(defaultMood?.code ?? null);
  }, [controlledSelectedCode, defaultMood?.code, selectedCode, storefrontMoods]);

  const selectMood = (mood: StorefrontMood) => {
    if (controlledSelectedCode === undefined) setSelectedCode(mood.code);
    const preference = { moodDefinitionId: mood.id, requestedLocale: mood.locale };
    setSelectedMood(preference);
    setCartMood.mutate(preference);
    onSelectionChange?.(mood);
  };

  return (
    <section className="mood-selector" id="mood" aria-labelledby="mood-heading">
      <div className="mood-heading-row">
        <div>
          <h2 id="mood-heading">Hôm nay bạn cảm thấy thế nào ?</h2>
          <p>
            {showSuggestions
              ? 'Chọn mood để nhận gợi ý đồ uống và thông điệp dành riêng cho bạn.'
              : 'Chọn mood để lưu cùng giỏ hàng và đơn hàng của bạn.'}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="mood-list mood-list-loading" role="status" aria-label="Đang tải mood">
          {Array.from({ length: 4 }, (_, index) => (
            <span className="mood-card mood-card-skeleton" key={index} />
          ))}
        </div>
      ) : isError ? (
        <section className="menu-empty mood-empty" role="status">
          <span aria-hidden="true">!</span>
          <h2>Chưa tải được mood</h2>
          <p>Vui lòng thử lại. Trang không tự tạo mood thay cho dữ liệu từ hệ thống.</p>
          <button type="button" onClick={retry}>
            Thử lại
          </button>
        </section>
      ) : storefrontMoods.length === 0 ? (
        <section className="menu-empty mood-empty" role="status">
          <span aria-hidden="true">☁</span>
          <h2>Chưa có mood công khai</h2>
          <p>Cửa hàng chưa xuất bản mood nào cho khách hàng.</p>
        </section>
      ) : (
        <>
          <fieldset className="mood-list">
            <legend className="visually-hidden">Chọn mood hiện tại</legend>
            {storefrontMoods.map((mood) => (
              <MoodCard
                key={mood.code}
                mood={mood}
                selected={selected?.code === mood.code}
                onSelect={selectMood}
              />
            ))}
          </fieldset>
          {showSuggestions && selected && (
            <MoodSuggestion
              mood={selected}
              recommendations={recommendations}
              loading={recommendationsQuery.isPending}
              error={recommendationsQuery.isError}
            />
          )}
          {setCartMood.isError && (
            <p role="alert">Khong the cap nhat mood cho gio hang. Vui long thu lai.</p>
          )}
        </>
      )}
    </section>
  );
}
